import { faSpinnerThird as fadSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons';
import { useFeatureGate } from '@statsig/react-bindings';
import { useAtom } from 'jotai';

import { Button, Icon, Text, cn } from '@growtherapy/sprout-ui';

import { ContinuityOfCareBanner, ContinuityOfCareBannerProps } from '@grow-therapy-team/seedling-components';

import { useTheme } from '../../../hooks/useTheme';
import { Theme } from '../../../state/theme';
import { showClientCocBannerGate } from '../../../statsig/gates';
import { mightNeedToRefreshAppointmentListAtom } from '../../schedule-appointment/state';
import { formatShortAppointmentDate, formatTimeRange, getBrowserTimezone } from '../../schedule-appointment/utils';
import { TimelineAppointment } from '../../useGetUpcomingAppointments';
import { AddAppointmentButtonAndReferLink } from './AddAppointmentButtonAndReferLink';
import { RefreshAppointmentsCallout } from './RefreshAppointmentsCallout';

export function UpcomingClientAppointmentsTab({
  upcomingAppointments,
  isLoading,
  onAppointmentClick,
  onClickAddAppointment,
  onRefresh,
  isClientUserAccountPaused,
  referClientLink,
  eapCoverageCard,
  clientFirstName,
}: {
  upcomingAppointments: TimelineAppointment[];
  isLoading?: boolean;
  onAppointmentClick: (appointment: TimelineAppointment) => void;
  onClickAddAppointment: () => void;
  onRefresh?: () => void;
  isClientUserAccountPaused: boolean;
  referClientLink: string;
  eapCoverageCard?: {
    continuityOfCareAcknowledged?: boolean | null;
    remainingSessions?: number | null;
    payorName?: string | null;
    maxSessions?: number | null;
    payorLink?: string | null;
  } | null;
  clientFirstName?: string | null;
}) {
  const [mightNeedToRefreshAppointmentList, setMightNeedToRefreshAppointmentList] = useAtom(
    mightNeedToRefreshAppointmentListAtom,
  );
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;

  const { value: showClientCocBanner } = useFeatureGate(showClientCocBannerGate);

  if (isLoading) {
    return (
      <div className='flex justify-center'>
        <Icon
          aria-hidden={false}
          aria-label='Loading...'
          size='xl'
          className={cn('text-lilac-700 fa-spin', {
            'rebrand:text-text-inverse': isDarkTheme,
          })}
          icon={fadSpinnerThird}
        />
      </div>
    );
  }

  const bannerProps: ContinuityOfCareBannerProps | null =
    eapCoverageCard && !!eapCoverageCard.payorName && !!clientFirstName
      ? {
          eapCoverageCard: {
            continuityOfCareAcknowledged: eapCoverageCard.continuityOfCareAcknowledged ?? false,
            remainingSessions: eapCoverageCard.remainingSessions ?? 0,
            payorName: eapCoverageCard.payorName,
            maxSessions: eapCoverageCard.maxSessions ?? 0,
            payorLink: eapCoverageCard.payorLink ?? undefined,
          },
          clientFirstName,
        }
      : null;

  if (!upcomingAppointments || upcomingAppointments.length === 0) {
    return (
      <div>
        {bannerProps && showClientCocBanner && <ContinuityOfCareBanner {...bannerProps} />}
        <p
          className={cn({
            'rebrand:text-text-inverse': isDarkTheme,
          })}
        >
          No appointments scheduled.
        </p>
        <AddAppointmentButtonAndReferLink
          onClickAddAppointment={onClickAddAppointment}
          isClientUserAccountPaused={isClientUserAccountPaused}
          referClientLink={referClientLink}
          className='mt-4'
        />
      </div>
    );
  }

  return (
    <div>
      {bannerProps && showClientCocBanner && <ContinuityOfCareBanner {...bannerProps} />}
      {mightNeedToRefreshAppointmentList && (
        <RefreshAppointmentsCallout
          onClickRefresh={() => {
            setMightNeedToRefreshAppointmentList(false);
            onRefresh?.();
          }}
        />
      )}
      <ul>
        {upcomingAppointments.map((appointment) => {
          const isRecurringAppointment = appointment?.recurringAppointmentGroupingId;
          return (
            <li key={appointment.id}>
              <Button
                use='unstyled'
                className='text-left px-0 py-4 w-full'
                onClick={() => {
                  onAppointmentClick(appointment);
                }}
              >
                <Text className='font-medium rebrand:text-text-inverse'>
                  {formatShortAppointmentDate(appointment?.timeStart)}
                  {isRecurringAppointment && (
                    <Icon aria-hidden={false} aria-label='(recurring appointment)' icon={faRepeat} className='ml-2' />
                  )}
                </Text>
                <Text className='text-neutral-700 font-normal rebrand:text-text-inverse'>
                  <span className='sr-only'>from</span>
                  {formatTimeRange(appointment.timeStart, appointment.timeEnd)} {getBrowserTimezone()}
                </Text>
              </Button>
              <hr className='text-neutral-600 rebrand:text-border-subtle arc:text-border-default' />
            </li>
          );
        })}
      </ul>
      <AddAppointmentButtonAndReferLink
        onClickAddAppointment={onClickAddAppointment}
        isClientUserAccountPaused={isClientUserAccountPaused}
        referClientLink={referClientLink}
        className='mt-4'
      />
    </div>
  );
}
